// src/MapComponent.js
import React, { useRef, useEffect, useState } from "react";
import { LoadingOverlay, Banner } from "./components/common";
import { useMapView } from "./hooks/map/useMapView";
import { useNearestFeature } from "./hooks/map/useNearestFeature";
import { useAddressSearch } from "./hooks/map/useAddressSearch";
import { useMapReset } from "./hooks/map/useMapReset";
import "./EstimatorTool.css";

function MapComponent({
  serviceType,
  setEstimate,
  setSelectedPoint,
  onRadiusUpdate,
  onToast,
  onNearestSummary,
  requiredPhase,
  resetSignal,
  onOpIdChange,
  onSearchingChange,
  isGeneratingOrSubmitting = false,
  generatingMessage = "Calculating…",
}) {
  const mapDiv = useRef(null);
  const [isImagery, setIsImagery] = useState(false);
  const [maxRadiusMeters] = useState(32186);

  // Initialize map view and layers
  const { viewRef, clickLayerRef, resultsLayerRef } = useMapView({
    mapDiv,
    onBasemapReady: setIsImagery,
  });

  // Nearest feature detection
  const {
    isSearching,
    runNearestForPoint,
    captureMapSnapshot,
    lastPointRef,
    abortRef,
    pendingArgsRef,
    inFlightRef,
  } = useNearestFeature({
    viewRef,
    clickLayerRef,
    resultsLayerRef,
    requiredPhase,
    maxRadiusMeters,
    setEstimate,
    setSelectedPoint,
    onRadiusUpdate,
    onToast,
    onNearestSummary,
    onOpIdChange,
    onSearchingChange,
  });

  // Address search functionality
  const { searchText, setSearchText, geocodeAndDrop, onSubmitSearch, addressError, clearAddressError } = useAddressSearch({
    viewRef,
    onToast,
    runNearestForPoint,
    lastPointRef,
  });

  // Map reset functionality
  useMapReset({
    resetSignal,
    viewRef,
    clickLayerRef,
    resultsLayerRef,
    lastPointRef,
    onNearestSummary,
  });

  // Clear address error on reset/start over
  useEffect(() => {
    if (resetSignal) {
      clearAddressError?.();
    }
  }, [resetSignal, clearAddressError]);

  // Handle basemap toggle
  useEffect(() => {
    const view = viewRef.current;
    if (view?.map) {
      view.map.basemap = isImagery ? "hybrid" : "streets-vector";
    }
  }, [isImagery]);

  /*
   * Handle map clicks - user clicks to place pin
   * Stores click point and triggers nearest infrastructure search
   * Results shown as blue radius circle on map
   * Search finds transformer/pole within 5 miles (or maxRadiusMeters config)
   */
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const handleClick = async (event) => {
      const clickPoint = event.mapPoint.clone();
      lastPointRef.current = clickPoint.clone();
      clearAddressError?.();
      await runNearestForPoint(clickPoint, { emitSelectedPoint: true });
    };

    const clickHandle = view.on("click", handleClick);

    // Expose geocode function for external use (address search bar, etc.)
    window.__GEOCODE_AND_DROP = geocodeAndDrop;

    return () => {
      clickHandle?.remove();
      try {
        delete window.__GEOCODE_AND_DROP;
      } catch {}
    };
  }, [runNearestForPoint, geocodeAndDrop, lastPointRef, clearAddressError]);

  /*
   * Auto re-query when user changes phase selection
   * User clicks map → selects 1-phase → changes to 3-phase
   * Need to re-search for 3-phase infrastructure (might be different location/distance)
   * Abort in-flight search to avoid race conditions (stale results)
   */
  useEffect(() => {
    const point = lastPointRef.current;
    const view = viewRef.current;
    if (!point || !view) return;

    // Queue latest request and abort in-flight
    pendingArgsRef.current = { clickPoint: point, emitSelectedPoint: false };
    try {
      abortRef.current?.abort();
    } catch {}

    // If nothing running, kick it now
    if (!inFlightRef.current) {
      runNearestForPoint(point, { emitSelectedPoint: false });
    }
  }, [requiredPhase, runNearestForPoint]);

  /*
   * Expose map snapshot function for PDF generation
   * Called by useSubmitActions when user clicks "Generate Estimate"
   * Captures current map view (pin + radius circle) as base64 image
   * Included in PDF to show service location visually
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__CAPTURE_MAP_SNAPSHOT = captureMapSnapshot;
    }
    return () => {
      try {
        delete window.__CAPTURE_MAP_SNAPSHOT;
      } catch {}
    };
  }, [captureMapSnapshot]);

  return (
    <div className="mapWrapper">
      <div className={`mapDiv ${isSearching ? "mapDiv--dim" : ""}`} ref={mapDiv} />

      {(isSearching || isGeneratingOrSubmitting) && (
        <LoadingOverlay 
          message={isGeneratingOrSubmitting ? generatingMessage : "Calculating…"} 
          ariaLabel={isGeneratingOrSubmitting 
            ? (generatingMessage.includes("Submitting") ? "Map interactions disabled during request submission" : "Map interactions disabled during estimate generation")
            : "Searching nearby infrastructure"
          } 
        />
      )}

      <div className="map-overlay" aria-label="Search and status">
        <form className="map-search" onSubmit={onSubmitSearch} title="Search an address">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search address…"
            aria-label="Search address"
          />
          <button type="submit" className="mapButton" title="Geocode and drop pin">
            Search
          </button>
        </form>
        {addressError && (
          <Banner message={addressError} kind="error" />
        )}
      </div>

      <div className="mapControls" aria-label="Map controls">
        <button
          type="button"
          className="mapButton"
          onClick={() => setIsImagery((v) => !v)}
          aria-pressed={isImagery}
          title={isImagery ? "Switch to Streets" : "Switch to Aerial (Imagery + Labels)"}
        >
          {isImagery ? "Streets" : "Aerial"}
        </button>
      </div>
    </div>
  );
}

export default MapComponent;
